import { createSlice } from "@reduxjs/toolkit";
import { logout } from "configs/store-actions";
import { AuthUser, IUser } from "apis/user/models/user";
import { userAuthApi } from "apis/user/controllers/user-auth";
import { userApi } from "apis/user/controllers/user";
import { AccountType } from "apis/account/enums/account-type";
import { accountSelfApi } from "apis/account/controllers/account-self";
import { DateUtil } from "utils/date-util";
import { authenticationPasswordApi } from "apis/authentication/controllers/authentication-password";
import { authenticationApi } from "apis/authentication/controllers/authentication";
import { AuthenticationResponse } from "apis/authentication/dtos/responses/authentication-response";
import { authenticationUserIdentifierApi } from "apis/authentication/controllers/authentication-user-identifier";
import { authenticationOauthApi } from "apis/authentication/controllers/authentication-oauth";
import { ApiResponse } from "types/api";
import { userIdentifierApi } from "apis/user/controllers/user-identifier";
import { UserIdentifierType } from "apis/user/enums/user-identifier-type";
import { userSettingApi } from "apis/user/controllers/user-setting";

type InitialState = {
  authUser: AuthUser;
  isSideNavigation: boolean;
  isIconSidebar: boolean;
  isLoading: boolean;
};

export const initialState: InitialState = {
  authUser: null,
  isSideNavigation: false,
  isIconSidebar: true,
  isLoading: false,
};

export const slice = createSlice({
  name: "global",
  initialState: initialState,
  reducers: {
    setAuthUser: (state, { payload }) => {
      state.authUser = payload;
    },
    toggleSideNavigation: (state, { payload }) => {
      state.isSideNavigation =
        payload !== undefined ? !!payload : !state.isSideNavigation;
    },
    toggleIconSidebar: (state, { payload }) => {
      state.isIconSidebar =
        payload !== undefined ? !!payload : !state.isIconSidebar;
    },
    toggleLoading: (state, { payload }) => {
      state.isLoading = payload !== undefined ? !!payload : !state.isLoading;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(logout, () => ({ ...initialState }))
      .addMatcher(
        authenticationPasswordApi.endpoints.signInPasswordAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationOauthApi.endpoints.signInOauthAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationOauthApi.endpoints.signUpOauthAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationOauthApi.endpoints.authenticateOauthAuthentication
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationUserIdentifierApi.endpoints
          .verifyUserIdentifierVerificationAuthentication.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        authenticationApi.endpoints.refreshAuthentication.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromAuthenticationResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthApi.endpoints.verifyUserAuthEmailAddressVerification
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthApi.endpoints.signupUserAuth.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthApi.endpoints.verifyUserAuthEmailAddressVerification
          .matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthApi.endpoints.ssoUserAuth.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userAuthApi.endpoints.signinUserAuth.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...getAuthUserFromResponse(payload),
          } as AuthUser;
        }
      )
      .addMatcher(
        userIdentifierApi.endpoints.getSelfUserIdentifier.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            userIdentifier: payload.data,
          } as AuthUser;
        }
      )
      .addMatcher(
        userIdentifierApi.endpoints.getPrimaryTypeUserIdentifier.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            userEmailAddressIdentifier:
              payload.data.type === UserIdentifierType.EmailAddress
                ? payload.data
                : state.authUser?.userEmailAddressIdentifier,
            userPhoneNumberIdentifier:
              payload.data.type === UserIdentifierType.PhoneNumber
                ? payload.data
                : state.authUser?.userPhoneNumberIdentifier,
          } as AuthUser;
        }
      )
      .addMatcher(
        userApi.endpoints.getUserSelf.matchFulfilled,
        (state, { payload }) => {
          state.authUser = {
            ...state.authUser,
            details: payload.data,
            user: payload.data,
          } as AuthUser;
        }
      )
      .addMatcher(
        accountSelfApi.endpoints.getAccountSelf.matchFulfilled,
        (state, { payload }) => {
          const accountId = payload.data.id;
          const accountType = payload.data.type;

          state.authUser = {
            ...state.authUser,
            account: payload.data,
            accountClient: payload.data.client,
            accountFreelancer: payload.data.freelancer,
            accountId,
            accountType,
            isClient: accountType == AccountType.Client,
            isFreelancer: accountType == AccountType.Freelancer,
            accountDataNormalized: {},
            accountClientDataNormalized: {},
            accountFreelancerDataNormalized: {},
            // accountDataNormalized: normalizeArray(payload.data?.data || [], {
            //   getKey: ({ key }) => key,
            //   getValue: ({ value }) => value,
            // }),
            // accountClientDataNormalized: normalizeArray(
            //   payload.data?.client?.data || [],
            //   { getKey: ({ key }) => key, getValue: ({ value }) => value }
            // ),
            // accountFreelancerDataNormalized: normalizeArray(
            //   payload.data?.freelancer?.data || [],
            //   { getKey: ({ key }) => key, getValue: ({ value }) => value }
            // ),
          } as AuthUser;
        }
      ),
});

export const { setAuthUser, toggleIconSidebar, toggleLoading } = slice.actions;

export default slice;

export function getStorageState({ authUser }: typeof initialState) {
  return { authUser };
}

function getAuthUserFromAuthenticationResponse(
  payload: ApiResponse<AuthenticationResponse>
) {
  return {
    id: payload.data?.id,
    expiresIn: String(DateUtil.addSeconds(new Date(), 3600)),
    isAuthenticated: !!payload?.access_token,
    accessToken: payload?.access_token,
    accessTokenExpirationDate: payload?.access_token_expiration_date,
    refreshToken: payload?.refresh_token,
    refreshTokenExpirationDate: payload?.refresh_token_expiration_date,
    accountId: payload.data?.account_id,
  } as AuthUser;
}

function getAuthUserFromResponse(payload: ApiResponse<IUser>) {
  // const accountId = payload.data.data?.find(
  //   (datum) => datum.key === "default_account_id"
  // )?.value;
  //
  // const accountType = payload.data.data?.find(
  //   (datum) => datum.key === "default_account_type"
  // )?.value;

  return {
    id: payload.data.id,
    details: payload.data,
    expiresIn: String(DateUtil.addSeconds(new Date(), 3600)),
    isAuthenticated: !!payload?.access_token,
    accessToken: payload.access_token,
    // accountId,
    // accountType,
    // isClient: accountType == AccountType.Client,
    // isFreelancer: accountType == AccountType.Freelancer,
  } as AuthUser;
}
